import "./narrative.scss";

// import _ from "lodash";
import orientations from "../../constants/orientations";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import tableDropAction from "../../constants/tableDropAction";
import { immutableRecords } from "../../types";
import reduceRect from "../../utils/reduceRect";
import selectors from "./selectors";

const {
  NARRATIVE_CARD_CHILD_X_MARGIN,
  NARRATIVE_CARD_CHILD_Y_MARGIN,
  NARRATIVE_CARD_DRAG_DROP_BORDER_ALLOWANCE,
  NARRATIVE_CARD_Y_PADDING
} = Dimensions;

const pointIsInsideRect = (rect, point) =>
  point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

const findNarrativeDropPosition = (
  cards,
  itemId,
  multipleItemIds,
  itemCenter,
  parent,
  orientation,
  fontSize,
  typesAllowedNarrativeChildren,
  narrativeIndexById
) => {
  const BORDER_ALLOWANCE = scalePixelsToFontSize(NARRATIVE_CARD_DRAG_DROP_BORDER_ALLOWANCE, fontSize);
  const SCALED_CHILD_X_MARGIN = scalePixelsToFontSize(NARRATIVE_CARD_CHILD_X_MARGIN, fontSize);
  const SCALED_CHILD_Y_MARGIN = scalePixelsToFontSize(NARRATIVE_CARD_CHILD_Y_MARGIN, fontSize);

  const ADD_X_MARGIN = orientation === orientations.HORIZONTAL ? SCALED_CHILD_X_MARGIN / 2 : SCALED_CHILD_X_MARGIN;
  const ADD_Y_MARGIN = orientation === orientations.HORIZONTAL ? SCALED_CHILD_Y_MARGIN : SCALED_CHILD_Y_MARGIN / 2;

  // Which value will we care about comparisons for later?
  const centerValue = orientation === orientations.HORIZONTAL ? itemCenter.x : itemCenter.y;

  // Work out if we are on top of a card (or the margin around a card), which we will treat as the
  // card for the sake of this calculation

  let card =
    orientation === orientations.HORIZONTAL
      ? cards.find(c => c.id != itemId && itemCenter.x < c.rect.right + ADD_X_MARGIN)
      : cards.find(c => c.id != itemId && itemCenter.y < c.rect.bottom + ADD_Y_MARGIN);

  // If we did not find a card, see if we are above the first card, and use that instead of the bottom
  if (!card && cards.size) {
    const firstCard = cards.first();
    const firstCardCenter =
      orientation === orientations.HORIZONTAL
        ? firstCard.rect.left + firstCard.rect.width / 2
        : firstCard.rect.top + firstCard.rect.height / 2;
    if (centerValue < firstCardCenter) {
      card = firstCard;
    }
  }

  // If we are not on top of any card at all, then we just want to add it into this card by adding it at
  // the bottom.
  if (!card) {
    return {
      dropTargetId: parent,
      dropAction: tableDropAction.DropOnRow,
      targetParent: parent,
      targetIndex: cards.size,
      targetOrientation: orientation
    };
  }

  // Otherwise, check if we want to add INTO that card, in which case we need to call recursively
  // to make sure we are at the deepest level of the tree
  if (typesAllowedNarrativeChildren.indexOf(card.itemDisplay.type) >= 0) {
    const xBorder = orientation === orientations.HORIZONTAL ? BORDER_ALLOWANCE : 0;
    const yBorder = orientation === orientations.HORIZONTAL ? 0 : BORDER_ALLOWANCE;
    const reducedRect = reduceRect(card.rect, xBorder, yBorder);

    // Are we inside the reduced hit area we need?
    if (pointIsInsideRect(reducedRect, itemCenter)) {
      // Before calling recursively, adjust itemCenter so that it is relative to this new
      // card position.
      const cardHeight = card.itemDisplay.itemCardSize.height + 2 * NARRATIVE_CARD_Y_PADDING;
      const nextCenter = {
        x: itemCenter.x - card.rect.left - NARRATIVE_CARD_CHILD_X_MARGIN,
        y: itemCenter.y - card.rect.top - cardHeight - NARRATIVE_CARD_CHILD_Y_MARGIN
      };
      return findNarrativeDropPosition(
        card.cards,
        itemId,
        multipleItemIds,
        nextCenter,
        card.id,
        card.orientation,
        fontSize,
        typesAllowedNarrativeChildren,
        narrativeIndexById
      );
    }
  }

  // If we reach here, then we have a target card and we are adding above or below.
  const cardCenter =
    orientation === orientations.HORIZONTAL
      ? card.rect.left + card.rect.width / 2
      : card.rect.top + card.rect.height / 2;

  const dropAction = centerValue < cardCenter ? tableDropAction.DropAboveRow : tableDropAction.DropBelowRow;
  const cardIndex = cards.indexOf(card);

  return {
    targetParent: parent,
    targetIndex: dropAction === tableDropAction.DropAboveRow ? cardIndex : cardIndex + 1,
    targetOrientation: orientation,
    dropTargetId: card.id,
    dropAction
  };
};

export const onHoverCard = (props, dragItem, positions, _domNode, canDropItems) => {
  const targetPointerPos = positions.targetPointerPos;
  const adjustedMousePos = {
    x: targetPointerPos.x,
    y: targetPointerPos.y
  };

  const canDrop = canDropItems.canDrop;
  if (!canDrop) {
    return {
      message: canDropItems.message,
      messageType: "error"
    };
  }

  const itemCenter = adjustedMousePos;

  const pos = findNarrativeDropPosition(
    props.displayData.cards,
    dragItem.itemId,
    dragItem.multipleItemIds,
    itemCenter,
    null,
    props.displayData.orientation,
    props.fontSize,
    props.typesAllowedNarrativeChildren,
    props.narrativeIndexById
  );

  const draggedCard = selectors.getNarrativeViewDataForItem(
    props.localStore,
    props.viewId,
    props.displayData,
    dragItem.itemId
  );

  const dropPosition = immutableRecords.NarrativeDropPositionRecord({
    ...pos,
    sourceParent: draggedCard ? draggedCard.parent : null,
    sourceIndex: draggedCard ? draggedCard.index : props.displayData.cards.size,
    draggedId: dragItem.itemId,
    draggedSize: immutableRecords.SizeRecord({
      width: draggedCard.rect.width,
      height: draggedCard.rect.height
    })
  });

  props.onInternalMoveCard(dragItem.multipleItemIds, dropPosition, dragItem);
};

export const onDropCard = (props, _dragItem, _positions, _domNode) => {
  // When dropping, we just tell it to commit whatever last happened with the hover...
  props.onDispatchLastMoveAction();
};
